<!-- filepath: c:\Users\TyreseMakhubele\Documents\Other\P\trendztube\src\views\legal\TermsOfService.vue -->
<template>
  <AppHeader />
  <div class="min-h-screen bg-gradient-to-br from-primary-lighter via-white to-primary-lighter pt-8 pb-20 px-4">
    <div class="max-w-4xl mx-auto">
      <!-- Header -->
      <div class="text-center mb-12">
        <h1 class="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
          Terms of Service
        </h1>
        <p class="text-lg text-gray-600">
          Last updated: {{ lastUpdated }}
        </p>
      </div>

      <!-- Content -->
      <div class="bg-white rounded-2xl shadow-lg p-8 space-y-8">
        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Agreement to Terms</h2>
          <p class="text-gray-700 leading-relaxed">
            By accessing and using TrendzTube ("Service," "we," "our," or "us"), you accept and agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service.
          </p>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Description of Service</h2>
          <p class="text-gray-700 leading-relaxed mb-4">
            TrendzTube is an AI-powered platform that provides YouTube video idea generation based on trending content analysis. Our Service includes:
          </p>
          <ul class="text-gray-700 space-y-2 list-disc pl-6">
            <li>Real-time YouTube trending video analysis</li>
            <li>AI-generated video ideas and recommendations</li>
            <li>Region and category-specific content suggestions</li>
            <li>Usage tracking and subscription management</li>
            <li>User profile and account management</li>
          </ul>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">User Accounts</h2>
          
          <h3 class="text-xl font-semibold text-gray-800 mb-3">Registration Requirements</h3>
          <ul class="text-gray-700 space-y-2 mb-4 list-disc pl-6">
            <li>You must be at least 13 years old to use our Service</li>
            <li>You must provide accurate and complete registration information</li>
            <li>You are responsible for maintaining the security of your account</li>
            <li>You agree to notify us immediately of any unauthorized use</li>
          </ul>

          <h3 class="text-xl font-semibold text-gray-800 mb-3">Account Responsibilities</h3>
          <p class="text-gray-700 leading-relaxed">
            You are solely responsible for all activity that occurs under your account. You agree to use strong passwords and not share your account credentials with others.
          </p>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Subscription Plans and Billing</h2>
          
          <h3 class="text-xl font-semibold text-gray-800 mb-3">Available Plans</h3>
          <div class="space-y-4 mb-4">
            <div class="bg-gray-50 rounded-lg p-4">
              <h4 class="font-semibold text-gray-800 mb-2">Free Plan</h4>
              <ul class="text-gray-700 text-sm space-y-1 list-disc pl-4">
                <li>3 generations per month (18 video ideas total)</li>
                <li>All regions and categories</li>
                <li>Email support</li>
              </ul>
            </div>
            <div class="bg-gray-50 rounded-lg p-4">
              <h4 class="font-semibold text-gray-800 mb-2">Starter Plan - $5.99/month</h4>
              <ul class="text-gray-700 text-sm space-y-1 list-disc pl-4">
                <li>100 generations per month (1000 video ideas total)</li>
                <li>Basic AI recommendations</li>
                <li>Email support</li>
              </ul>
            </div>
            <div class="bg-gray-50 rounded-lg p-4">
              <h4 class="font-semibold text-gray-800 mb-2">Pro Plan - $19.99/month</h4>
              <ul class="text-gray-700 text-sm space-y-1 list-disc pl-4">
                <li>500 generations per month (3000 video ideas total)</li>
                <li>Premium AI recommendations</li>
                <li>Priority support</li>
              </ul>
            </div>
          </div>

          <h3 class="text-xl font-semibold text-gray-800 mb-3">Billing Terms</h3>
          <ul class="text-gray-700 space-y-2 list-disc pl-6">
            <li>Subscriptions are billed monthly in advance</li>
            <li>Usage limits reset every 30 days from your first generation</li>
            <li>Payments are processed securely through Lemon Squeezy</li>
            <li>All fees are non-refundable unless required by law</li>
            <li>Price changes will be communicated 30 days in advance</li>
          </ul>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Acceptable Use Policy</h2>
          
          <h3 class="text-xl font-semibold text-gray-800 mb-3">Permitted Uses</h3>
          <p class="text-gray-700 leading-relaxed mb-4">
            You may use our Service for legitimate content creation purposes, including generating video ideas for YouTube channels and content marketing.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3">Prohibited Activities</h3>
          <ul class="text-gray-700 space-y-2 list-disc pl-6">
            <li>Attempting to reverse engineer or copy our AI algorithms</li>
            <li>Using automated tools to exceed usage limits</li>
            <li>Sharing account credentials with others</li>
            <li>Creating fake or multiple accounts to bypass limits</li>
            <li>Using the Service for illegal or harmful content creation</li>
            <li>Attempting to access our systems without authorization</li>
            <li>Violating any applicable laws or regulations</li>
          </ul>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Intellectual Property</h2>
          
          <h3 class="text-xl font-semibold text-gray-800 mb-3">Our Rights</h3>
          <p class="text-gray-700 leading-relaxed mb-4">
            TrendzTube and all related trademarks, service marks, and trade names are owned by us. Our AI algorithms, software, and platform design are protected by intellectual property laws.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3">Your Rights</h3>
          <p class="text-gray-700 leading-relaxed mb-4">
            You retain ownership of any content you create using our generated video ideas. We do not claim ownership of your final videos or content.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3">YouTube Data</h3>
          <p class="text-gray-700 leading-relaxed">
            We access YouTube's trending data through their official API in compliance with their Terms of Service. Users must also comply with YouTube's Terms when using generated ideas.
          </p>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Service Availability</h2>
          <p class="text-gray-700 leading-relaxed mb-4">
            While we strive for high availability, we do not guarantee that our Service will be uninterrupted or error-free. We may:
          </p>
          <ul class="text-gray-700 space-y-2 list-disc pl-6">
            <li>Perform scheduled maintenance</li>
            <li>Update our algorithms and features</li>
            <li>Temporarily limit access during high demand</li>
            <li>Modify or discontinue features with notice</li>
          </ul>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Limitation of Liability</h2>
          <p class="text-gray-700 leading-relaxed mb-4">
            To the maximum extent permitted by law, TrendzTube shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our Service.
          </p>
          <p class="text-gray-700 leading-relaxed">
            Our total liability to you shall not exceed the amount you paid for the Service in the 12 months preceding the claim.
          </p>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Disclaimer of Warranties</h2>
          <p class="text-gray-700 leading-relaxed">
            Our Service is provided "as is" without warranties of any kind. We do not guarantee that generated video ideas will result in viral content or channel growth. Success depends on various factors including content quality, timing, and audience engagement.
          </p>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Termination</h2>
          
          <h3 class="text-xl font-semibold text-gray-800 mb-3">By You</h3>
          <p class="text-gray-700 leading-relaxed mb-4">
            You may cancel your subscription at any time from your account settings. Cancellation takes effect at the end of your billing period.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3">By Us</h3>
          <p class="text-gray-700 leading-relaxed">
            We may terminate or suspend your account immediately if you violate these Terms or engage in prohibited activities. Upon termination, your access to the Service will cease immediately.
          </p>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Governing Law</h2>
          <p class="text-gray-700 leading-relaxed">
            These Terms are governed by and construed in accordance with applicable laws. Any disputes shall be resolved through binding arbitration or in the appropriate courts.
          </p>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Changes to Terms</h2>
          <p class="text-gray-700 leading-relaxed">
            We reserve the right to modify these Terms at any time. Material changes will be communicated via email or prominent notice on our website. Continued use of the Service after changes constitutes acceptance of new Terms.
          </p>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
          <p class="text-gray-700 leading-relaxed">
            For questions about these Terms of Service, please contact us:
          </p>
          <div class="bg-gray-50 rounded-lg p-4 mt-4">
            <p class="text-gray-700">
              Email: <a href="mailto:tyteapps@gmail.com" class="text-primary hover:underline">tyteapps@gmail.com</a><br>
              Subject: Terms of Service Inquiry
            </p>
          </div>
        </section>

        <section>
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Severability</h2>
          <p class="text-gray-700 leading-relaxed">
            If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full force and effect.
          </p>
        </section>
      </div>
    </div>
  </div>
  <AppFooter />
</template>

<script>
import AppHeader from '@/components/common/AppHeader.vue';
import AppFooter from '@/components/common/AppFooter.vue';

export default {
  name: 'TermsOfService',
  components: {
    AppHeader,
    AppFooter
  },
  data() {
    return {
      lastUpdated: 'November 8, 2025'
    };
  }
};
</script>

<style scoped>
section {
  scroll-margin-top: 2rem;
}
</style>